import { ActivityStatus } from '../../interfaces/activity'
import { OngoingReportingStatus } from '../../interfaces/bbc'
import { BoxSignStatusLabel, MANUALLY_SEND_LABEL } from '../../interfaces/box'
import { AmendmentAnalysisStatus } from '../../interfaces/generalAnalysis'
import { TaskSource, TaskStatus } from '../../interfaces/task'
import { FilterFieldType, IFilter, UNMAPPED_FILTER_VALUE } from './types'

// The Company / Org column's value: the task's company (company_id, else its
// client_name mirror), else its organization, else its person's primary company or
// organization. Reads the aliases joinTaskCompanyOrOrg (taskCompanyProjection.ts)
// adds. NULLIF skips an empty name, as the display does. ONE line, for the same
// TypeORM rewrite reason as TASK_ASSIGNEE_NAME_SQL.
export const TASK_COMPANY_OR_ORG_NAME_SQL = `COALESCE(NULLIF(company.name, ''), NULLIF(task.client_name, ''), NULLIF(organization.name, ''), NULLIF(personAffiliationCompany.name, ''), NULLIF(personAffiliationOrganization.name, ''))`

// Display name of a task's assignee, mirroring the client's `formatAssigneeLabel`
// (nickname, else first name, else email) so a sorted run reads in the order the
// cells show. Requires an `assignedToUser` join, selected as `assigneeName`.
// NULL for an unassigned task, which lands it last.
//
// Keep it on ONE line. TypeORM's alias→column rewrite captures the property name
// with `[^ =(),]+`, which does not exclude newlines — so a reference at the end of
// a wrapped line is captured as `email\n`, misses the lookup, and reaches Postgres
// as bare `assignedToUser.email`, whose alias then case-folds to `assigneduser`.
export const TASK_ASSIGNEE_NAME_SQL = `COALESCE(NULLIF(TRIM(assignedToUser.nickname), ''), NULLIF(TRIM(assignedToUser.firstName), ''), assignedToUser.email)`

// The Assignee filter's "no assignee" entry, added to the user roster the pages
// build at render time. `UNMAPPED_FILTER_VALUE` rather than `''` because
// sanitizeMyTasksFilters reads an empty string as a CLEARED filter, while the
// sentinel survives it and reaches Base.buildFiltersQuery as `IS NULL`. Labelled
// to match the read-only assignee cell rather than the generic "Unmapped".
export const TASK_UNASSIGNED_FILTER_OPTION = {
  value: UNMAPPED_FILTER_VALUE,
  label: 'Unassigned',
}

// Category is a custom field, but unlike a project's dynamic `cf_<uuid>` columns
// this one is a known, static entry in the config — so it keeps a plain key
// (also what it was before the migration, so saved filter URLs still resolve).
// The client pairs this key with the field's id, fetched at runtime, when it
// builds `customFieldFilters`.
export const TASK_CATEGORY_FILTER_FIELD = 'category'

// Every source a task can carry. Enum-derived, so a new source needs no edit here.
export const TASK_SOURCE_OPTIONS = Object.values(TaskSource).map((source) => ({
  value: source,
  label: source,
}))

// Every status a task can hold = the task-owned set ∪ each source workflow's own
// status enum ∪ the funding manual-send label (the runtime mirror of the
// `TaskStatusValue` type). Deduped because the task-owned values alias some source
// values (e.g. Complete). A new source = add one `...Object.values(NewStatus)` line.
export const TASK_STATUS_OPTIONS = [
  ...new Set<string>([
    ...Object.values(TaskStatus),
    ...Object.values(ActivityStatus),
    ...Object.values(OngoingReportingStatus).filter(
      (s) => s !== OngoingReportingStatus.Process && s !== OngoingReportingStatus.Verified,
    ),
    ...Object.values(BoxSignStatusLabel),
    // Executed mirrors onto Complete on the task, so it never appears as a row status.
    ...Object.values(AmendmentAnalysisStatus).filter((s) => s !== AmendmentAnalysisStatus.Executed),
    MANUALLY_SEND_LABEL,
  ]),
].map((status) => ({ value: status, label: status }))

export const MY_TASKS_LIST_FILTERS_CONFIG = [
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'My Tasks',
        // `assignedToUserId` is filled in at render time with the current
        // user's id (see MyTasksPage's filtersConfig memo).
        filters: { assignedToUserId: [] },
      },
      {
        title: 'Collaborating',
        // `collaboratingUserId` is filled in at render time with the current
        // user's id (same memo as My Tasks). Handled specially in ListMyTasks as
        // a superset — tasks the user is assigned to, created, or follows as a
        // collaborator — via an EXISTS over task_collaborators, not a plain
        // column filter, so it can't live in the column config below.
        filters: { collaboratingUserId: [] },
      },
    ],
  },
  {
    field: 'title',
    type: FilterFieldType.Text,
    dbField: 'title',
    alias: 'task',
    isSortable: true,
    // No defaultWidth: every other column is a percentage, so under
    // table-layout:fixed Task alone absorbs the remainder.
    minWidth: 120,
    title: 'Task',
    placeholder: 'Task name',
  },
  {
    // Category sits immediately right of the Task name: its cell deep-links to
    // the task's destination (TaskCategoryLink → onNavigateToActivity), so it
    // doubles as the one-click "open" affordance while staying self-labeling.
    //
    // No dbField/alias: category lives in the Category custom field, so this
    // filter is routed into `customFieldFilters` (matched on option id) and
    // sorting rides the `cf_<uuid>` orderBy path — buildFiltersQuery never
    // touches it.
    field: TASK_CATEGORY_FILTER_FIELD,
    type: FilterFieldType.Autocomplete,
    // Not a SQL column — `isCustom` makes buildFiltersQuery skip it for both
    // filtering and ordering, so a stray `cf_<uuid>` key can never reach the
    // query builder and be quoted as a column name.
    isCustom: true,
    isSortable: true,
    isMultiple: true,
    // ~10 characters of pill text; longer names ellipsize and show in full on
    // hover, or when the column is dragged wider.
    minWidth: 72,
    defaultWidth: '5.2%',
    title: 'Category',
    placeholder: 'Category',
  },
  {
    // Where the task came from — an icon-only column right of Category, so the
    // header carries the label and the cell just shows the glyph. Sorting orders
    // by the stored TaskSource string, which groups each source's rows together —
    // that grouping is what sorting an icon column is for, and it needs no join
    // because the label the icon stands for IS the column value.
    field: 'source',
    type: FilterFieldType.List,
    dbField: 'source',
    alias: 'task',
    isSortable: true,
    isMultiple: true,
    title: 'Source',
    // Icon-only cell, so below the uniform resize floor — but not so far below
    // that the header's label and its sort caret collide once it is the active
    // sort (the caret only takes space on hover or on the sorted column).
    minWidth: 72,
    defaultWidth: '3.76%',
    options: TASK_SOURCE_OPTIONS,
  },
  {
    // Project membership lives in task_project_placements, not on a tasks
    // column, so this is applied by hand in ListMyTasks (like
    // collaboratingUserId) rather than through buildFiltersQuery.
    //
    // The column renders one pill per live placement. Not sortable: a task can
    // sit in several projects at once, so there's no single value to order by.
    field: 'projectIds',
    type: FilterFieldType.Autocomplete,
    isCustom: true,
    isMultiple: true,
    isSortable: false,
    minWidth: 110,
    defaultWidth: '9.5%',
    title: 'Projects',
    placeholder: 'Project',
  },
  {
    // The Company / Org column, by NAME (see TASK_COMPANY_OR_ORG_NAME_SQL). The
    // picker still writes only task.company_id. The list queries
    // `addSelect(TASK_COMPANY_OR_ORG_NAME_SQL, 'clientName')`: on a paginated
    // (skip/take) query the framework orders by `field`, which resolves to that
    // select alias. Header sort clicks send `dbField` as the orderBy param, so the
    // API request carries this expression; buildFiltersQuery matches either spelling.
    // The key stays `clientName` so saved filter URLs still resolve.
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    dbField: TASK_COMPANY_OR_ORG_NAME_SQL,
    isSortable: true,
    isMultiple: true,
    minWidth: 90,
    defaultWidth: '10.45%',
    title: 'Company / Org',
    placeholder: 'Company / Org',
  },
  {
    // Recurrence is orthogonal to source (any task can be made recurring), so
    // it filters on its own. Expression dbField + no alias: the column is a uuid,
    // and the boolean filter compares whatever `dbField` evaluates to.
    // Sits after Company so the menu's first row is Category / Source / Company —
    // this entry is excludeColumn, so its position never affects column order.
    field: 'isRecurring',
    type: FilterFieldType.Boolean,
    dbField: '(task.recurrence_rule_id IS NOT NULL)',
    isSortable: false,
    excludeColumn: true,
    title: 'Recurring',
    // A Boolean filter renders only when it has options — FilterMenu falls
    // through to no component (and an empty slot) without them.
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    // The Assignee COLUMN. Split from its filter below — the filter compares a
    // uuid and the column has to sort by name — the same way CRM splits Owner.
    // The list queries `addSelect(TASK_ASSIGNEE_NAME_SQL, 'assigneeName')`: on a
    // paginated (skip/take) query the framework orders by `field`, which resolves
    // to that select alias.
    field: 'assigneeName',
    type: FilterFieldType.Text,
    dbField: TASK_ASSIGNEE_NAME_SQL,
    isSortable: true,
    excludeFilter: true,
    // The cell is the assignee's face alone — narrower than the uniform resize
    // floor allows, same as the icon-only Source column.
    minWidth: 56,
    defaultWidth: '4%',
    title: 'Assignee',
  },
  {
    // The Assignee FILTER — a user-id multi-select whose options the pages inject
    // at render time. Not a column; the entry above is what renders and sorts.
    field: 'assignedToUserId',
    type: FilterFieldType.Autocomplete,
    dbField: 'assigned_to_user_id',
    alias: 'task',
    isMultiple: true,
    excludeColumn: true,
    title: 'Assignee',
    placeholder: 'Assignee',
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    dbField: 'status',
    alias: 'task',
    isSortable: true,
    isMultiple: true,
    minWidth: 100,
    defaultWidth: '7.84%',
    title: 'Status',
    options: TASK_STATUS_OPTIONS,
  },
  {
    field: 'dueDate',
    type: FilterFieldType.Date,
    dbField: 'due_date',
    alias: 'task',
    isSortable: true,
    minWidth: 85,
    defaultWidth: '6.78%',
    title: 'Due Date',
  },
  {
    field: 'createdAt',
    type: FilterFieldType.Date,
    dbField: 'created_at',
    alias: 'task',
    isSortable: true,
    minWidth: 85,
    defaultWidth: '7.33%',
    title: 'Created Date',
  },
  {
    // Empty on every open task, so boards start with it hidden and My Tasks leaves it out.
    field: 'completedAt',
    type: FilterFieldType.Date,
    dbField: 'completed_at',
    alias: 'task',
    isSortable: true,
    minWidth: 85,
    defaultWidth: '7.33%',
    title: 'Completed Date',
  },
] as const satisfies IFilter[]
